'use client'; // Add this to ensure the component is client-side

import { LOGO_URL } from '@/constant/assets';

import { Star, Truck } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import party from 'party-js';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import '../app/Homepage.css';
import SvgIcons from '@/components/SvgIcons';
import { Testimonials } from '@/components/Testimonials';

export const revalidate = 60;


export const generateMetadata = async ({
  params,
}: {
  params: { slug: string };
}) => {
  return {
    title: `Contact - Clean Flow System`,
    description: `Get in touch with Clean Flow Systems for inquiries, quotes, or support on cleanroom equipment and lab solutions. Our team is ready to assist you.`,
    openGraph: {
      title: `Gallery - Clean Flow System`,
      description: `Reach out to Clean Flow Systems for expert guidance on cleanroom installations and lab equipment. Send us a message or call us today.`,
      images: [
        {
          url: LOGO_URL,
          width: 1200,
          height: 630,
          alt: 'Tamil Printed Tshirts',
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      title: `Gallery - Clean Flow System`,
      description: `Have a question about our cleanroom or lab equipment? Contact Clean Flow Systems — we're happy to help with quotes, support, and more.`,
      images: [
        LOGO_URL,
      ],
    },
  };
};

const ContactUs = () => {
  // Controlled form state
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobile: '',
    company: '',
    location: '',
    message: '',
    honey: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission status
  const formRef = useRef<HTMLFormElement>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Validation function
  const validate = () => {
    const errs: { [key: string]: string } = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.mobile.trim()) errs.mobile = 'Mobile number is required';
    else if (!/^\d{10}$/.test(form.mobile)) errs.mobile = 'Enter a valid 10-digit mobile number';
    if (!form.company.trim()) errs.company = 'Company is required';
    if (!form.location.trim()) errs.location = 'Location is required';
    if (!form.message.trim()) errs.message = 'Message is required';
    if (form.honey) errs.honey = 'Spam detected';
    if (!captchaToken) errs.captcha = 'Please complete the CAPTCHA';
    return errs;
  };
  const [clientIp, setClientIp] = useState<string>('');

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then(res => res.json())
      .then(data => setClientIp(data.ip))
      .catch(() => setClientIp(''));
  }, []);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      setIsSubmitting(true);
      try {
        const myHeaders = new Headers();
        myHeaders.append('Content-Type', 'application/json');
        myHeaders.append(
          'Authorization',
          'Basic Q2xlYW5GbG93U3lzdGVtNzUxNjM1Mzo0U1VIR0dHVVM3NTRER1lTNUVTSFVTWUZE'
        );

        const raw = JSON.stringify({
          form_type: 1,
          company_name: form.company,
          name: form.name,
          mobile: form.mobile,
          email: form.email,
          message: form.message,
          location: form.location,
          client_info: JSON.stringify({
            browser: navigator.userAgent,
            os: navigator.platform,
            device: (() => {
              const ua = navigator.userAgent;
              if (/mobile/i.test(ua)) return 'Mobile';
              if (/tablet|ipad|playbook|silk/i.test(ua)) return 'Tablet';
              if (/android/i.test(ua) && !/mobile/i.test(ua)) return 'Tablet';
              if (/Macintosh/i.test(ua) && 'ontouchend' in document) return 'Tablet';
              if (/Windows NT/i.test(ua) && /Touch/i.test(ua)) return 'Tablet';
              return 'Desktop';
            })(),
            ip: clientIp,
          }),
          captcha_token: captchaToken, // Include CAPTCHA token if required by API
        });

        const requestOptions = {
          method: 'POST',
          headers: myHeaders,
          body: raw,
          redirect: 'follow' as const,
        };

        const response = await fetch(
          'https://clientconnect.vilvabusiness.com/api/cleanflowsystem/submit-form-request',
          requestOptions
        );
        const result = await response.text();
        console.log('API Response:', result);

        // Format message for WhatsApp (with bold-like labels using * *)
        const whatsappMessage =
          `*New Contact Form Submission*\n\n` +
          `*Name:* ${form.name}\n` +
          `*Email:* ${form.email}\n` +
          `*Mobile:* ${form.mobile}\n` +
          `*Company:* ${form.company}\n` +
          `*Location:* ${form.location}\n` +
          `*Comments/Message:* ${form.message || "No comments provided"}\n`;

        // Your WhatsApp number (international format, no spaces or dashes)
        const phoneNumber = "917358426440"; // +91 removed as wa.me expects it without +

        // Create WhatsApp URL
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(whatsappMessage)}`;

        // Open WhatsApp link in a new tab
        window.open(whatsappUrl, "_blank");

        setSubmitted(true);
        if (formRef.current) {
          party.confetti(formRef.current, { count: party.variation.range(40, 60) });
        }

        // Reset form after celebration
        setTimeout(() => {
          setForm({
            name: '',
            email: '',
            mobile: '',
            company: '',
            location: '',
            message: '',
            honey: '',
          });
          setSubmitted(false);
          setCaptchaToken(null); // Reset CAPTCHA token
          turnstileRef.current?.reset();
        }, 2000);
      } catch (error) {
        console.error('API Error:', error);
        setErrors({ api: 'Failed to submit form. Please try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const siteKey = '0x4AAAAAABhnoWzkfK2Wo_Wz'; // Replace with your actual Cloudflare Turnstile site key

  return (
    <>


      <div className="contact_form_al">
        <div className="px-4 lg:container mx-auto py-5">
          <div className="row g-5">
            <div className="col-md-5 col-12">
              <div className="form_container_all ">
                <div className=" service_head">
                  <h1 className=''>We’re Here to Help</h1>
                  <p className=''>
                    Have a question or need assistance? Get in touch with our team today! We’re happy to
                    provide more information about our products and services or assist with any inquiries
                    you may have. Whether you're looking for support or a quote, we’re just a message or
                    call away.
                  </p>
                </div>
                <div className="form_cont my-3">
                  <form
                    className="contact_form"
                    ref={formRef}
                    onSubmit={handleSubmit}
                    autoComplete="off"
                  >
                    {/* Name Field */}
                    <div className="form_group">
                      <label htmlFor="name">Name</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        placeholder="Enter your name"
                        required
                        value={form.name}
                        onChange={handleChange}
                      />
                      {errors.name && <span className="error">{errors.name}</span>}
                    </div>
                    {/* Email and Mobile Number */}
                    <div className="form_group dual_input">
                      <div>
                        <label htmlFor="email">Email</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          placeholder="Enter your email"
                          required
                          value={form.email}
                          onChange={handleChange}
                        />
                        {errors.email && <span className="error">{errors.email}</span>}
                      </div>
                      <div>
                        <label htmlFor="mobile" className="mt-md-0 mt-3">
                          Mobile Number
                        </label>
                        <input
                          type="tel"
                          id="mobile"
                          name="mobile"
                          placeholder="Enter your mobile number"
                          required
                          value={form.mobile}
                          onChange={handleChange}
                        />
                        {errors.mobile && <span className="error">{errors.mobile}</span>}
                      </div>
                    </div>
                    {/* Company Field */}
                    <div className="form_group">
                      <label htmlFor="company">Company</label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Enter your company name"
                        required
                        value={form.company}
                        onChange={handleChange}
                      />
                      {errors.company && <span className="error">{errors.company}</span>}
                    </div>
                    {/* Location Field */}
                    <div className="form_group">
                      <label htmlFor="location">Location</label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        placeholder="Enter your location"
                        required
                        value={form.location}
                        onChange={handleChange}
                      />
                      {errors.location && <span className="error">{errors.location}</span>}
                    </div>
                    {/* Enter Message Field */}
                    <div className="form_group">
                      <label htmlFor="message">Message</label>
                      <textarea
                        id="message"
                        name="message"
                        placeholder="Enter your message"
                        required
                        value={form.message}
                        onChange={handleChange}
                      ></textarea>
                      {errors.message && <span className="error">{errors.message}</span>}
                    </div>
                    {/* Honeypot Field (hidden from users) */}
                    <div style={{ display: 'none' }}>
                      <label htmlFor="honey">Leave this field empty</label>
                      <input
                        type="text"
                        id="honey"
                        name="honey"
                        value={form.honey}
                        onChange={handleChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                      {errors.honey && <span className="error">{errors.honey}</span>}
                    </div>

                    {isMounted && (
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "0x4AAAAAABhnoWzkfK2Wo_Wz"}
                        options={{ theme: "light" }}
                        onSuccess={(token) => setCaptchaToken(token)}
                        onExpire={() => setCaptchaToken(null)}
                        onError={() => setCaptchaToken(null)}
                      />
                    )}

                    {errors.captcha && <span className="error">{errors.captcha}</span>}
                    {errors.api && <span className="error">{errors.api}</span>}

                    <div className="mt-4">
                      <button className="contact_btn w-full" type="submit" disabled={submitted || isSubmitting} style={{ width: '100%', border: 'none' }}>
                        {submitted
                          ? 'Submitted!'
                          : isSubmitting
                            ? 'Submitting...'
                            : 'Send Request'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="col-md-6 col-12 ">
              <div className="map_location">
                <div className="row">
                  <div className="col-lg-6 col-12">
                    <div className="google_map">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d28154.912255704596!2d80.11981767722177!3d12.906187825586441!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a525f21cdd8bea1%3A0xcf81fa57f8b5d072!2sSelaiyur%2C%20Chennai%2C%20Tamil%20Nadu!5e1!3m2!1sen!2sin!4v1735885911075!5m2!1sen!2sin"
                        width="100%"
                        height="230px"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    </div>
                  </div>
                  <div className="col-lg-6 col-12 mt-lg-0 mt-3">
                    <div className="loc_details">
                      <p>
                        <SvgIcons.ContLocation /> No. 14, Muthamizh Street, Chelli Nagar Main Road,
                        Selaiyur, Chennai - 6000 073
                      </p>
                      <p>
                        <SvgIcons.Contcall /> 044 - 6454 0816
                      </p>
                      <p>
                        <SvgIcons.Contofficecall /> 78100 05665
                      </p>
                      <p className="!mb-0 !pb-0">
                        <SvgIcons.Contmail /> cleanflowsystems@gmail.com
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="contact_testi py-4">
        <Testimonials />
      </div>
    </>
  );
};

export default ContactUs;